import GoldenRockBetBarComponent from "goldenrock/components/betbar/golden-rock-bet-bar-component";
import GoldenRockSpinButtonRingComponent from "goldenrock/components/betbar/golden-rock-spin-button-ring-component";
import { GoldenRockCustomLayers } from "goldenrock/names";
import quickfireFreeGamesComponent from "quickfire/components/quickfire-free-games-component";
import QuickfireInit from "quickfire/states/quickfire-init";
import { BetBarType } from "slotworks/components/betbar/bet-bar-component";
import Components from "slotworks/components/components";
import StandardReelSpinnerResultBuffer from "slotworks/components/matrix/reel/transition-behaviours/spin/standard-reel-spinner-result-buffer";
import PromptContent from "slotworks/components/prompt/prompt-content";
import canvasManager from "slotworks/graphics/canvas/canvas-manager";
import Layers from "slotworks/graphics/layers/layers";
import Loader from "slotworks/loader/loader";
import GameState from "slotworks/model/game-state";
import SpinRecord from "slotworks/model/gameplay/records/spin-record";
import SettingsModel from "slotworks/model/settings/settings-model";
import { Services } from "slotworks/services/services";
import AutoSmartUIGroup from "slotworks/smart-ui/auto-smart-ui-group";
import SmartUIFlags, { SmartUIFlag } from "slotworks/smart-ui/smart-ui-flags";
import SmartUIManager from "slotworks/smart-ui/smart-ui-manager";
import Contract from "slotworks/utils/contracts/contract";
import Sequence from "slotworks/utils/contracts/sequence";
import deviceInfo from "slotworks/utils/device-info";
import ControlOptions from "slotworks/smart-ui/options/control-options";
import BetModel from 'slotworks/model/bets/bet-model';
import GoldenRockControlList from "goldenrock/controls/golden-rock-control-list";

export default class GoldenRockInit extends QuickfireInit {
    public static SETTINGS_LAYERS: Layers[] = [
        Layers.MatrixContent,
        Layers.Banners,
        Layers.MatrixForeground,
        Layers.MatrixBackground,
        Layers.SymbolAnimations,
        Layers.CelebrationBackground,
        Layers.CelebrationContent,
        Layers.CelebrationForeground
    ];

    public complete() {
        this.setupComponents();
        this.setupLayerOrder();
        this.setupSharedSmartUI();
        this.setupChineseLogo();
        this.fixDOMScrolling();

        if (deviceInfo.isDesktop) {
            this.setupDesktopLayerBehaviour();
            this.setupDesktopSmartUI();
        } else {
            this.setupMobileLayerBehaviour();
            this.setupMobileSmartUI();
            this.addTotalStakeOptions();
        }

        this.setLastWinnings();
        // TODO: Quickfire specific code shouldn't be in here, in case we need to port games to other platforms
        quickfireFreeGamesComponent.onDiscard.add(() => this.setLastWinnings(0));

        // Force games to remove buffer symbols from results. Experimental, may become default
        StandardReelSpinnerResultBuffer.customBufferSize = 0;

        new Sequence([
            () => this.showQuickfireDialogs(),
            () => this.showOnboard(),
            () => Contract.wrap(() => super.complete())
        ]).execute();
    }

    protected showOnboard() {
        return new Contract<void>((resolve) => {
            if (Layers.Prompts.hasScene("onboard")) {
                const isRecovery = (GameState.getCurrentGame() && GameState.getCurrentGame().isRecovered);

                if (!isRecovery) {
                    SmartUIFlags.set(SmartUIFlag.IDLE, true);
                    SmartUIFlags.set(SmartUIFlag.PROMPT, true);

                    Services.transaction.setBalance(GameState.getCurrentGame().balance);

                    Layers.BetBar.hide();
                    Layers.Footer.hide();
                    Layers.SmartButton.hide();

                    const onboard = new PromptContent(["onboard"], Infinity);
                    onboard.setClickToSkip(true);

                    Components.prompt.showPrompt(onboard).then(() => {
                        Layers.BetBar.show();
                        Layers.SmartButton.show();
                        Layers.Footer.show();

                        Loader.unloadStage(2);

                        SmartUIFlags.set(SmartUIFlag.PROMPT, false);

                        resolve();
                    });

                    this.showCorrectLogo(Layers.Prompts);

                } else {
                    Loader.unloadStage(2);
                    resolve();
                }
            } else {
                resolve();
            }
        });
    }

    protected setupComponents(): void {
        GoldenRockSpinButtonRingComponent.init();
        GoldenRockControlList.init();
        Layers.Custom.get(GoldenRockCustomLayers.MENUS_BACKGROUND).hide();

        this.setAutoresizeTotalStake();

        Layers.BetBar.onSceneEnter.add(() => {
            this.setAutoresizeTotalStake();
        });
    }

    protected setupLayerOrder(): void {
        if (deviceInfo.isDesktop) {
            Layers.Custom.get(GoldenRockCustomLayers.ONBOARD_BLOCKER).moveBehind(Layers.BetBar);
        }
        Layers.Custom.get(GoldenRockCustomLayers.MENUS_BACKGROUND).moveBehind(Layers.MenusContent);
        Layers.Footer.moveBehind(Layers.SmartButton);
    }

    protected setupDesktopLayerBehaviour(): void {
        Layers.MenusContent.onSceneEnter.add((scene: string) => {
            if (scene === "paytable" || scene === "gamesettings") {
                Layers.Custom.get(GoldenRockCustomLayers.MENUS_BACKGROUND).show();
                if (!Layers.Background.currentSceneIs("anticipation_mode")) {
                    Layers.Background.setScene("blurred").execute();
                }
                GoldenRockInit.SETTINGS_LAYERS.forEach((layer: Layers) => layer.hide());
            }
        });
        Layers.MenusContent.onSceneExit.add((scene: string) => {
            if (scene === "paytable" || scene === "gamesettings") {
                Layers.Custom.get(GoldenRockCustomLayers.MENUS_BACKGROUND).hide();
                if (!Layers.Background.currentSceneIs("anticipation_mode")) {
                    Layers.Background.setScene("default").execute();
                }
                GoldenRockInit.SETTINGS_LAYERS.forEach((layer: Layers) => layer.show());
            }
        });
    }

    protected setupMobileLayerBehaviour(): void {
        Layers.Custom.get(GoldenRockCustomLayers.MENUS_BACKGROUND).hide();

        Layers.MenusContent.onSceneEnter.add((scene: string) => {
            if (["paytable", "gamesettings", "rules"].indexOf(scene) !== -1) {
                this.showMenuGameState(GoldenRockBetBarComponent.MENU, "default");
                Layers.SmartButton.setScene("menu").execute();
            } else if (scene === "total_stake_panel") {
                this.showMenuGameState(GoldenRockBetBarComponent.TOTAL_STAKE_MENU, "total_stake_panel");
                Layers.SmartButton.setScene("menu").execute();
            } else if (scene === "autoplay_ukgc") {
                this.showMenuGameState(GoldenRockBetBarComponent.MENU, "autoplay_ukgc");
            }
        });

        Layers.MenusContent.onSceneExit.add((scene: string) => {
            if (["paytable", "gamesettings", "rules", "total_stake_panel", "autoplay_ukgc"].indexOf(scene) !== -1) {
                this.resetMenuGameState();
            }
        });
    }

    protected showMenuGameState(betbarType: BetBarType, backgroundType: string): void {
        const menusBackgroundLayer: Layers = Layers.Custom.get(GoldenRockCustomLayers.MENUS_BACKGROUND);

        menusBackgroundLayer.show();
        menusBackgroundLayer.setScene(backgroundType).execute();
        Components.betBar.setType(betbarType);
        Layers.Footer.setScene("menu").execute();
        if (deviceInfo.isDesktop) {
            canvasManager.canvas.style.filter = "blur(5px)";
        }
        if (!Layers.Background.currentSceneIs("anticipation_mode")) {
            Layers.Background.setScene("blurred").execute();
        }
    }

    protected resetMenuGameState(): void {
        Layers.Custom.get(GoldenRockCustomLayers.MENUS_BACKGROUND).hide();
        Components.betBar.setType(BetBarType.DEFAULT);
        Layers.SmartButton.setScene("default").execute();
        Layers.Footer.setScene("default").execute();
        canvasManager.canvas.style.removeProperty("filter");
        if (!Layers.Background.currentSceneIs("anticipation_mode")) {
            Layers.Background.setScene("default").execute();
        }
    }

    protected setupSharedSmartUI(): void {
        const settingsGroups = [];

        const totalStakePanelGroup: AutoSmartUIGroup = SmartUIManager.newSmartUIGroup(Layers.MenusContent, "total_stake_panel");
        SmartUIManager.registerButtonToGroup("total_stake", Layers.BetBar, ["default"], totalStakePanelGroup);
        totalStakePanelGroup.addDisableCondition([SmartUIFlag.FREE_BETS]);
        settingsGroups.push(totalStakePanelGroup);

        const autoplaySelect: AutoSmartUIGroup = SmartUIManager.newSmartUIGroup(Layers.SmartButton, "auto_select");
        SmartUIManager.addAutoShowGroupCondition(autoplaySelect, [SmartUIFlag.AUTO_SPIN_SELECT]);
        settingsGroups.push(totalStakePanelGroup);

        const spinToStartGroup: AutoSmartUIGroup = SmartUIManager.newSmartUIGroup(Layers.SmartButton, "freespins_start");
        SmartUIManager.addAutoShowGroupCondition(spinToStartGroup, [SmartUIFlag.FREE_SPINS, SmartUIFlag.PROMPT]);

        this.addSettingsGroupConditions(settingsGroups);
    }

    protected setupDesktopSmartUI(): void {
        const paytableGroup: AutoSmartUIGroup = SmartUIManager.newSmartUIGroup(Layers.MenusContent, "paytable");
        SmartUIManager.registerButtonToGroup("hamburger", Layers.BetBar, ["default", "freespins"], paytableGroup);

        const gameSettingsGroup: AutoSmartUIGroup = SmartUIManager.newSmartUIGroup(Layers.MenusContent, "gamesettings");
        SmartUIManager.registerButtonToGroup("settings", Layers.BetBar, ["default", "freespins"], gameSettingsGroup);

        const autoplayGroup: AutoSmartUIGroup = SmartUIManager.newSmartUIGroup(Layers.MenusContent, "autoplay_ukgc");
        SmartUIManager.registerButtonToGroup("autoplay", Layers.SmartButton, ["freespins", "default", "auto", "auto_select"], autoplayGroup);
        autoplayGroup.setFlagsOnShow([SmartUIFlag.AUTO_SPIN_SELECT]);
        if (!Services.autoplay.isEnabled()) {
            autoplayGroup.addDisableCondition([SmartUIFlag.ALWAYS]);
        }

        // Responsible gaming button
        SmartUIManager.newSmartUIGroup(Layers.PaytableContent, "page_2");

        this.addSettingsGroupConditions([gameSettingsGroup, paytableGroup, autoplayGroup]);
    }

    protected setupMobileSmartUI(): void {
        const settingsGroups = [];

        const menusBackgroundLayer: Layers = Layers.Custom.get(GoldenRockCustomLayers.MENUS_BACKGROUND);

        const paytableGroup: AutoSmartUIGroup = SmartUIManager.newSmartUIGroup(Layers.MenusContent, "paytable");
        SmartUIManager.registerButtonToGroup("hamburger", Layers.SmartButton, ["default"], paytableGroup);
        SmartUIManager.registerButtonToGroup("paytable", menusBackgroundLayer, ["default"], paytableGroup);
        settingsGroups.push(paytableGroup);

        const autoplayGroup: AutoSmartUIGroup = SmartUIManager.newSmartUIGroup(Layers.MenusContent, "autoplay_ukgc");
        SmartUIManager.registerButtonToGroup("auto_button", Layers.SmartButton, ["freespins", "default", "auto", "auto_select"], autoplayGroup);
        autoplayGroup.setFlagsOnShow([SmartUIFlag.AUTO_SPIN_SELECT]);
        if (!Services.autoplay.isEnabled()) {
            autoplayGroup.addDisableCondition([SmartUIFlag.ALWAYS]);
        }

        settingsGroups.push(autoplayGroup);

        const rulesGroup: AutoSmartUIGroup = SmartUIManager.newSmartUIGroup(Layers.MenusContent, "rules");
        SmartUIManager.registerButtonToGroup("rules", menusBackgroundLayer, ["default"], rulesGroup);
        settingsGroups.push(rulesGroup);

        const gameSettingsGroup: AutoSmartUIGroup = SmartUIManager.newSmartUIGroup(Layers.MenusContent, "gamesettings");
        SmartUIManager.registerButtonToGroup("settings", menusBackgroundLayer, ["default"], gameSettingsGroup);
        settingsGroups.push(gameSettingsGroup);

        this.addSettingsGroupConditions(settingsGroups);

        // qf_home button
        SmartUIManager.newSmartUIGroup(menusBackgroundLayer, "default");
    }

    protected setupChineseLogo(): void {
        for (const layer of [Layers.Banners, Layers.Prompts, Layers.Custom.get("MenusBackground"), Layers.Custom.get("QuickfireFreeGames")]) {
            layer.onSceneEnter.add(() => this.showCorrectLogo(layer));
            this.showCorrectLogo(layer);
        }
    }

    protected setLastWinnings(win?: number): void {
        const currentGame = GameState.getCurrentGame();

        if (!isNaN(win)) {
            (Components.betBar as GoldenRockBetBarComponent).setLastWinnings(0);
        } else if (currentGame && !currentGame.isRecovered) {
            const record = currentGame.getCurrentRecord() as SpinRecord;
            if (record && record.cashWon) {
                (Components.betBar as GoldenRockBetBarComponent).setLastWinnings(record.cashWon);
            }
        }
    }

    protected showCorrectLogo(layer: Layers): void {
        ["", "portrait_"].forEach((prefix) => {
            const logo = layer.getSprite(prefix + "logo");
            const zhLogo = layer.getSprite(prefix + "logo_zh");

            if (logo && zhLogo) {
                if (SettingsModel.language.indexOf("zh") > -1) {
                    logo.visible = false;
                    zhLogo.visible = true;
                } else {
                    logo.visible = true;
                    zhLogo.visible = false;
                }
            }
        });
    }

    private setAutoresizeTotalStake() {
        const text = Layers.BetBar.getFlexiText("total_stake_stepper_value");
        if (text) {
            text.setAutoResize(true);
        }
    }

    private addSettingsGroupConditions(groups: AutoSmartUIGroup[]): void {
        for (const settingsGroup of groups) {
            settingsGroup.addDisableCondition([SmartUIFlag.FREE_SPINS]);
            settingsGroup.addDisableCondition([SmartUIFlag.PROMPT]);
            SmartUIManager.addAutoHideGroupCondition(settingsGroup, [], [SmartUIFlag.IDLE]);
            SmartUIManager.addAutoHideGroupCondition(settingsGroup, [SmartUIFlag.PROMPT]);
        }
    }

    private addTotalStakeOptions(): void {
        const radioContainers = Array.from(document.querySelectorAll("#total_stake_panel #total_stake_radios"));
        const options = new ControlOptions();
        BetModel.injectBetOptions(options);
        const betOptions = options.getTotalStakeValues();
        for (const container of radioContainers) {
            for (let i = 1; i < betOptions.length + 1; i++) {
                container.insertAdjacentHTML("beforeend", `
                    <div id="total_stake_radio_${i}" class="total_stake_radio">
                        <p id="total_stake_radio_${i}_label" class="total_stake_radio_label"></p>
                    </div>
                `);
            }
        }
    }

    private fixDOMScrolling(): void {
        for (const scrollArea of document.querySelectorAll("#scrollArea") as any) {
            scrollArea.addEventListener("touchstart", (e: TouchEvent) => e.stopPropagation());
            scrollArea.addEventListener("touchmove", (e: TouchEvent) => e.stopPropagation());
        }

        for (const gamesettings of document.querySelectorAll("#gamesettings") as any) {
            gamesettings.addEventListener("touchmove", (e: TouchEvent) => {
                if (e.target && (e.target as any).id === "volume_slider") {
                    return;
                }

                // todo: remove coupling between V and goldenrock
                try {
                    if (V.API.Fullscreen && V.API.Fullscreen.isOverlayVisible()) {
                        e.preventDefault();
                        e.stopPropagation();
                    }
                } catch (e) {
                    //
                }
            }, true);
        }
    }
}
